import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    LayoutDashboard,
    Calendar,
    FileText,
    Clock,
    FileCheck,
    UserCheck,
    Briefcase,
    DollarSign,
    Send,
    ClipboardList,
    Settings,
    LogOut,
    ChevronRight,
    ChevronLeft,
    Loader,
    Workflow,
    X, Building, CalendarDays, IterationCcw,
} from "lucide-react";
import "./Sidebar.css";
import logoCicada from "../../../images/logoCicada.png";
import { getSidebarMenusByRoleRequest } from "../../../redux/actions/sidebarMenuActions";
import { useLocation, useNavigate } from "react-router-dom";
import { logout } from "../../../redux/actions/authActions";

const Sidebar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [activeItem, setActiveItem] = useState(null);
    const [openSections, setOpenSections] = useState({});
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem("sidebarCollapsed");
        return saved === "true";
    });
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    // store
    const selectedRole = useSelector((state) => state.role?.selectedRole);
    const { menus = [], loading, error } = useSelector(
        (state) => state.sidebarMenus || {}
    );
    const currentUser = useSelector((state) => state?.auth?.user || state?.user?.currentUser);

    // Icons mapping
    const iconMap = useMemo(
        () => ({
            "layout-dashboard": LayoutDashboard,
            calendar: Calendar,
            "file-text": FileText,
            clock: Clock,
            "file-check": FileCheck,
            "user-check": UserCheck,
            briefcase: Briefcase,
            "dollar-sign": DollarSign,
            send: Send,
            "clipboard-list": ClipboardList,
            settings: Settings,
            "log-out": LogOut,
            info: FileText,
            building: Briefcase,
            work: Workflow,
            "conges-absences": Calendar,
            "jours-travail": Clock,
            "ordre-mission": Send,
            "notes-frais": DollarSign,
            attestations: FileCheck,
            "avances-prets": FileText,
            "avantages-sociaux": Briefcase,
            trombinoscope: UserCheck,
            organigramme: Workflow,
            "feuilles-temps": ClipboardList,
            missions: Briefcase,
            "documents-rh": FileText,
            "avance-prets": FileCheck,
            offboarding: IterationCcw,
            "mes-conges": Calendar,
            "mes-feuilles-temps": Clock,
            "mes-ordres-missions": Briefcase,
            "mes-fiches-paie": FileText,
            "mes-notes-frais": DollarSign,
            "mes-attestations": FileCheck,
            "mes-avances-prets": FileCheck,
            "mes-avantages-sociaux": Building,
            "demande-mission": Send,
            "agenda-equipe": CalendarDays,
        }),
        []
    );

    const getIcon = (iconName) => iconMap[iconName] || LayoutDashboard;

    // Fetch menus
    useEffect(() => {
        const roleId = selectedRole?.id;
        const companyId = currentUser?.companyId;
        if (roleId && companyId) {
            dispatch(getSidebarMenusByRoleRequest(companyId, roleId));
        }
    }, [dispatch, selectedRole?.id, currentUser?.companyId]);

    // Reset active item when role changes
    useEffect(() => {
        setActiveItem(null);
    }, [selectedRole?.id]);

    // Organize menus
    const { allMenus } = useMemo(() => {
        if (!menus || menus.length === 0) return { allMenus: [] };

        const visibleMenus = menus.filter((m) => m.displayStatus === "VISIBLE");

        const roots = visibleMenus
            .filter((m) => !m.parentMenuId)
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

        const childrenByParent = {};
        roots.forEach((root) => {
            const children = visibleMenus
                .filter((m) => m.parentMenuId === root.id)
                .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
            if (children.length > 0) {
                childrenByParent[root.id] = children;
            }
        });

        const allMenus = roots.map((root) => {
            const items = childrenByParent[root.id] || [];
            return {
                ...root,
                items,
                hasChildren: items.length > 0,
            };
        });

        return { allMenus };
    }, [menus]);

    // Auto-open sections
    useEffect(() => {
        if (!allMenus || allMenus.length === 0) return;
        setOpenSections((prev) => {
            const next = { ...prev };
            allMenus.forEach((menu) => {
                if (menu.hasChildren && typeof next[menu.id] === "undefined") {
                    next[menu.id] = true;
                }
            });
            return next;
        });
    }, [allMenus]);

    // Sync active item with path
    useEffect(() => {
        if (!menus?.length) return;
        const found = menus.find((m) => m.routePath === location.pathname);
        if (found?.id) setActiveItem(found.id);
    }, [location.pathname, menus]);

    // Save collapse state
    useEffect(() => {
        localStorage.setItem("sidebarCollapsed", isCollapsed);
    }, [isCollapsed]);

    useEffect(() => {
        const sidebarWidth = isMobileOpen ? 260 : isCollapsed ? 64 : 260;
        document.documentElement.style.setProperty("--app-sidebar-offset", `${sidebarWidth}px`);
        return () => {
            document.documentElement.style.removeProperty("--app-sidebar-offset");
        };
    }, [isCollapsed, isMobileOpen]);

    // Ajoutez cet useEffect pour détecter le redimensionnement de la fenêtre
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 1200) {
                // En mode mobile, fermer la sidebar et le menu mobile
                setIsCollapsed(true);
                setIsMobileOpen(false);
            } else {
                // En mode desktop, restaurer l'état sauvegardé
                const saved = localStorage.getItem("sidebarCollapsed");
                setIsCollapsed(saved === "true");
                setIsMobileOpen(false);
            }
        };

        // Appeler au montage pour définir l'état initial correct
        handleResize();

        // Écouter les changements de taille
        window.addEventListener('resize', handleResize);

        // Nettoyage
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handlers
    const handleItemClick = (menu) => {
        setActiveItem(menu.id);
        if (menu.routePath && menu.routePath.trim()) {
            navigate(menu.routePath);
        }
        // Close mobile menu on navigation
        if (window.innerWidth <= 1200) {
            setIsMobileOpen(false);
        }
    };

    const toggleSection = (sectionId) => {
        if (!isCollapsed && !isMobileOpen) {
            setOpenSections((prev) => ({
                ...prev,
                [sectionId]: !prev[sectionId],
            }));
        }
    };

    const handleLogout = () => {
        dispatch(logout());
        navigate("/");
    };

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const toggleMobileSidebar = () => {
        setIsMobileOpen(!isMobileOpen);
    };

    // UI states
    if (loading && menus.length === 0) {
        return (
            <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
                <div className="sidebar-loading">
                    <Loader className="spinner" size={24} />
                    {!isCollapsed && <p>Chargement...</p>}
                </div>
            </div>
        );
    }

    if (!selectedRole) {
        return (
            <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
                <div className="sidebar-loading">
                    <Settings size={24} />
                    {!isCollapsed && <p>Sélectionnez un rôle</p>}
                </div>
            </div>
        );
    }

    if (error && (!menus || menus.length === 0)) {
        return (
            <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
                <div className="sidebar-error">
                    <X size={24} />
                    {!isCollapsed && (
                        <>
                            <p>Erreur chargement</p>
                            <small>{String(error)}</small>
                        </>
                    )}
                </div>
            </div>
        );
    }

    if (allMenus.length === 0 && !loading) {
        return (
            <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
                <div className="sidebar-loading">
                    <FileText size={24} />
                    {!isCollapsed && <p>Aucun menu disponible</p>}
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Mobile overlay */}
            {isMobileOpen && (
                <div className="mobile-overlay" onClick={toggleMobileSidebar} />
            )}

            <div
                className={`sidebar ${isCollapsed ? "collapsed" : ""} ${
                    isMobileOpen ? "mobile-open" : ""
                }`}
            >
                {/* Header */}
                <div className="sidebar-header">
                    <div className="sidebar-logo">
                        <img
                            src={logoCicada}
                            alt="Cicada Logo"
                            className="logo-image logo-full"
                            onClick={() => navigate('/cards')}
                        />
                    </div>

                    {/* Bouton collapse - flèche gauche quand ouvert, droite quand fermé */}
                    <button
                        className="collapse-btn"
                        onClick={toggleSidebar}
                        aria-label={isCollapsed ? "Ouvrir la sidebar" : "Réduire la sidebar"}
                    >
                        {isCollapsed ? (
                            <ChevronRight size={20} />
                        ) : (
                            <ChevronLeft size={20} />
                        )}
                    </button>
                </div>

                {/* Menus */}
                <div className="sidebar-menu">
                    {allMenus.map((menu) => {
                        // Menu without children
                        if (!menu.hasChildren) {
                            const Icon = getIcon(menu.icon);
                            return (
                                <div key={menu.id} className="menu-section">
                                    <div
                                        className={`menu-item ${
                                            activeItem === menu.id ? "active" : ""
                                        }`}
                                        onClick={() => handleItemClick(menu)}
                                        title={menu.name}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <Icon className="menu-icon" size={20} />
                                        <span className="menu-label">{menu.name}</span>
                                    </div>
                                </div>
                            );
                        }

                        // Menu with children
                        return (
                            <div key={menu.id} className="menu-section">
                                <div
                                    className="section-header"
                                    onClick={() => toggleSection(menu.id)}
                                    role="button"
                                    tabIndex={0}
                                    title={menu.name}
                                >
                                    <span className="section-title">{menu.name}</span>
                                    {menu.items?.length > 0 && (
                                        <ChevronRight
                                            size={16}
                                            className={`chevron-icon ${
                                                openSections[menu.id] ? "open" : ""
                                            }`}
                                        />
                                    )}
                                </div>

                                {menu.items?.length > 0 && (
                                    <div
                                        className={`section-items ${
                                            openSections[menu.id] ? "open" : ""
                                        }`}
                                    >
                                        {menu.items.map((child) => {
                                            const Icon = getIcon(child.icon);
                                            return (
                                                <div
                                                    key={child.id}
                                                    className={`menu-item ${
                                                        activeItem === child.id ? "active" : ""
                                                    }`}
                                                    onClick={() => handleItemClick(child)}
                                                    title={child.name}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <Icon className="menu-icon" size={20} />
                                                    <span className="menu-label">{child.name}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Bottom */}
                <div className="sidebar-bottom">
                    <div
                        className="menu-item"
                        onClick={handleLogout}
                        title="Se déconnecter"
                        role="button"
                        tabIndex={0}
                    >
                        <LogOut className="menu-icon" size={20} />
                        <span className="menu-label">Se déconnecter</span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sidebar;
